import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';
import { MiscService } from '../../services/misc/misc.service';
import { DisplayCurrency } from '../onboarding-value-first/services/value-first-onboarding.types';
import { ValueFirstAgencyService } from './services/value-first-agency.service';
import { VfaWebsiteStep } from './molecules/website-step/website-step';
import { VfaManualStep } from './molecules/manual-step/manual-step';
import { VfaFoundStep } from './molecules/found-step/found-step';
import { VfaBrandsStep } from './molecules/brands-step/brands-step';
import { VfaAccountStep } from './molecules/account-step/account-step';
import { VfaCodeStep } from './molecules/code-step/code-step';
import { VfaWelcomeStep } from './molecules/welcome-step/welcome-step';
import { VfaWorkspacePanel } from './molecules/workspace-panel/workspace-panel';

const DISPLAY_CURRENCY_KEY = 'onboarding-display-currency';

@Component({
  selector: 'app-value-first-agency',
  imports: [
    VfaWebsiteStep,
    VfaManualStep,
    VfaFoundStep,
    VfaBrandsStep,
    VfaAccountStep,
    VfaCodeStep,
    VfaWelcomeStep,
    VfaWorkspacePanel,
  ],
  templateUrl: './value-first-agency.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass()' },
})
export class ValueFirstAgency implements OnInit, OnDestroy {
  readonly onboarding = inject(ValueFirstAgencyService);
  private readonly misc = inject(MiscService);
  private readonly document = inject(DOCUMENT);
  private readonly subscriptionList = new Subscription();

  constructor() {
    effect(() => {
      this.onboarding.step();
      this.document.defaultView?.scrollTo({ top: 0, left: 0 });
    });
  }

  ngOnInit(): void {
    this.onboarding.restore();
    this.document.defaultView?.scrollTo({ top: 0, left: 0 });
    this.loadDisplayCurrency();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  hostClass(): string {
    if (this.onboarding.step() === 'found') {
      return 'flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground';
    }
    return 'block min-h-dvh bg-background text-foreground';
  }

  frameClass(): string {
    if (this.onboarding.step() === 'found') {
      return 'flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground lg:flex-row';
    }
    return 'flex min-h-dvh flex-col bg-background text-foreground lg:flex-row';
  }

  columnClass(): string {
    if (this.onboarding.step() === 'found') {
      return 'flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden lg:w-1/2';
    }
    return 'flex min-h-dvh flex-1 flex-col lg:h-dvh lg:w-1/2 lg:overflow-y-auto';
  }

  mainClass(): string {
    if (this.onboarding.step() === 'found') {
      return 'mx-auto flex h-0 min-h-0 w-full max-w-lg flex-1 flex-col overflow-hidden px-5 py-6 pb-40 sm:px-8 lg:py-8 lg:pb-6';
    }
    return 'mx-auto flex w-full max-w-lg flex-1 flex-col px-5 py-8 pb-28 sm:px-8 sm:pb-12 lg:py-16';
  }

  goBack(): void {
    this.onboarding.goBack();
    this.document.defaultView?.scrollTo({ top: 0, left: 0 });
  }

  private loadDisplayCurrency(): void {
    const cached = this.readCachedCurrency();
    if (cached) {
      this.onboarding.setDisplayCurrency(cached);
    }
    this.subscriptionList.add(
      this.misc.getCountry().subscribe({
        next: (country) => {
          const next: DisplayCurrency = country === 'GB' ? 'GBP' : 'USD';
          this.onboarding.setDisplayCurrency(next);
          this.writeCachedCurrency(next);
        },
      }),
    );
  }

  private readCachedCurrency(): DisplayCurrency | null {
    try {
      const cached = this.document.defaultView?.localStorage.getItem(DISPLAY_CURRENCY_KEY);
      if (cached === 'GBP' || cached === 'USD') {
        return cached;
      }
    } catch {
      return null;
    }
    return null;
  }

  private writeCachedCurrency(currency: DisplayCurrency): void {
    try {
      this.document.defaultView?.localStorage.setItem(DISPLAY_CURRENCY_KEY, currency);
    } catch {
      // Ignore private-mode storage failures.
    }
  }
}
